import { useFormikContext } from 'formik';

import { RegistryId } from '@/react/portainer/registries/types/registry';

import { useAddon } from '../queries/useAddon';

import { InstallFormValues } from './types';

export interface AddonChartSource {
  /** The add-on's default, public chart reference from the catalog. */
  catalogChart: string;
  /** Registry the chart is pulled from; undefined means use `catalogChart`. */
  registryId?: RegistryId;
  /** Chart repository path within `registryId`; empty for the default source. */
  chart: string;
}

/**
 * useAddonChartSource resolves which chart the form is working against — the
 * source of truth for the version list, the values.yaml lookup and the displayed
 * source.
 *
 * When `fixed` (the detail view of an installed add-on) the persisted record is
 * authoritative, because the source cannot change once installed. It is
 * deliberately read from the record rather than the form: Formik captures its
 * initial values at mount, so a source that arrives on a later poll would
 * otherwise never be picked up and the form would keep resolving the default
 * chart. Otherwise the source is whatever the user has picked in the form.
 *
 * Must be called inside the install form's <Formik>.
 */
export function useAddonChartSource({
  addonId,
  fixed,
}: {
  addonId: string;
  fixed?: boolean;
}): AddonChartSource {
  const {
    values: { registryId, chart },
  } = useFormikContext<InstallFormValues>();
  const record = useAddon(addonId).data?.record;
  const catalogChart = record?.helmChartRepository ?? '';

  if (fixed) {
    return {
      catalogChart,
      registryId: record?.registryId,
      chart: record?.chartPath ?? '',
    };
  }

  return { catalogChart, registryId, chart };
}
